import type { ContractDetails } from '@/api/schemas';
import {
  CAPTION_CLASS,
  Field,
  LABEL_CLASS,
  VALUE_STRONG_CLASS,
} from '@/components/Field';
import { ProgressBar } from '@/components/ProgressBar';
import { Panel } from '@/components/ui/panel';
import { SectionHeading } from '@/components/ui/section-heading';
import { useClock } from '@/lib/clock';
import {
  elapsedPercent,
  expiryCaption,
  termInMonths,
  usagePercent,
} from '@/lib/contractTerm';
import { formatOdometer, formatQuantity, NOT_AVAILABLE } from '@/lib/display';
import { DateText } from '@/lib/formatDate';

const ROW_CLASS = 'grid grid-cols-1 gap-6 sm:grid-cols-3';

function MilesDriven({ contract }: { contract: ContractDetails }) {
  const recorded = contract.vehicle?.lastRecordedOdometer;
  const recordedAt = contract.vehicle?.lastRecordedAt;
  const limit = contract.contractEndOdometer;

  return (
    <div>
      <p className={LABEL_CLASS}>Miles Driven</p>
      {recorded === null || recorded === undefined ? (
        <p className={`mt-1 ${VALUE_STRONG_CLASS}`}>{NOT_AVAILABLE}</p>
      ) : (
        <>
          <p className={`mt-1 ${VALUE_STRONG_CLASS}`}>
            {recorded.toLocaleString()}
            <span className="text-[12px] font-normal text-[rgba(0,0,0,0.6)]">
              {' '}
              /{formatOdometer(limit)}
            </span>
          </p>
          <div className="mt-2">
            <ProgressBar percent={usagePercent(recorded, limit.value)} />
          </div>
          {recordedAt ? (
            <p className={CAPTION_CLASS}>
              as of <DateText value={recordedAt} />
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

export function TermsSection({ contract }: { contract: ContractDetails }) {
  const today = useClock().today();

  return (
    <div>
      <SectionHeading>Contract Terms</SectionHeading>
      <Panel variant="data" size="wide">
        <div className="space-y-6">
          <div className={ROW_CLASS}>
            <Field
              label="Term"
              value={`${termInMonths(contract.startDate, contract.expiryDate)} Months`}
            />
            <Field
              label="Contract Start Date"
              value={<DateText value={contract.startDate} />}
            />
            <div>
              <p className={LABEL_CLASS}>Contract End Date</p>
              <p className={`mt-1 ${VALUE_STRONG_CLASS}`}>
                <DateText value={contract.expiryDate} />
              </p>
              <div className="mt-2">
                <ProgressBar
                  percent={elapsedPercent(
                    contract.startDate,
                    contract.expiryDate,
                    today,
                  )}
                />
              </div>
              <p className={CAPTION_CLASS}>
                {expiryCaption(contract.expiryDate, today)}
              </p>
            </div>
          </div>

          <div className={ROW_CLASS}>
            <Field
              label="Mileage Limit"
              value={formatOdometer(contract.contractEndOdometer)}
            />
            <MilesDriven contract={contract} />
          </div>

          <div className={ROW_CLASS}>
            <Field
              label="Engine Hours Limit"
              value={formatQuantity(
                contract.contractEngineHoursLimit,
                'Engine Hours',
              )}
            />
            <Field
              label="Engine Hours Usage"
              value={formatQuantity(
                contract.vehicle?.lastRecordedEngineHours,
                'Engine Hours',
              )}
            />
          </div>
        </div>
      </Panel>
    </div>
  );
}
